import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import LogoBanner from './LogoBanner';
import { useTheme } from '../../contexts/ThemeContext';
import {
  NavScannerIcon,
  NavGainersIcon,
  NavLosersIcon,
  NavWatchlistIcon,
  NavAlertsIcon,
  NavEventsIcon,
} from '../icons/NavIcons';
import { useAlerts } from '../../context/AlertContext';

interface NavigationItem {
  name: string;
  path: string;
  badge?: number;
  category: 'DISCOVERY' | 'TRACKING' | 'EVENTS';
  icon: React.ReactNode;
  description?: string;
}

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
  scannerCount: number;
  gainersCount?: number; 
  losersCount?: number;
  watchlistCount?: number;
}

export default function Sidebar({ mobileOpen, onClose, scannerCount, gainersCount, losersCount, watchlistCount = 0 }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  useTheme();
  const { alerts } = useAlerts();

  const totalCount = alerts.length;
  
  const menuItems: NavigationItem[] = [
    { name: 'Scanner',       path: '/scanner', badge: scannerCount, category: 'DISCOVERY', icon: <NavScannerIcon />,   description: 'Active signals' },
    { name: 'Top Gainers',   path: '/scanner#gainers', badge: gainersCount ?? 0, category: 'DISCOVERY', icon: <NavGainersIcon />,   description: 'Overbought tickers' },
    { name: 'Top Losers',   path: '/scanner#losers',  badge: losersCount ?? 0, category: 'DISCOVERY', icon: <NavLosersIcon />,    description: 'Oversold tickers' },
    { name: 'Watchlist',    path: '/watchlist',        badge: watchlistCount,  category: 'TRACKING',  icon: <NavWatchlistIcon />, description: 'Tracked tickers' },
    { name: 'Alerts',   path: '/alerts',   badge: totalCount,  category: 'TRACKING',  icon: <NavAlertsIcon />,    description: 'Price triggers' },
    { name: 'Event Feed',   path: '/events',                     category: 'EVENTS',    icon: <NavEventsIcon />,    description: 'News & catalysts' },
  ];

  const categories: ('DISCOVERY' | 'TRACKING' | 'EVENTS')[] = ['DISCOVERY', 'TRACKING', 'EVENTS'];

  const categoryConfig = {
    DISCOVERY: {
      color:        'text-blue-500 dark:text-[#4f8ff8]',
      dot:          'bg-blue-500 dark:bg-[#4f8ff8]',
      activeBg:     'bg-blue-50 dark:bg-[#4f8ff8]/10',
      activeText:   'text-blue-700 dark:text-[#4f8ff8]',
      activeBar:    'from-[#4f8ff8] to-[#6366f1]',
      activeIcon:   'text-[#4f8ff8]',
      activeBadge:  'bg-[#4f8ff8]/20 text-[#4f8ff8]',
    },
    TRACKING: {
      color:        'text-violet-500 dark:text-[#a78bfa]',
      dot:          'bg-violet-500 dark:bg-[#a78bfa]',
      activeBg:     'bg-violet-50 dark:bg-[#a78bfa]/10',
      activeText:   'text-violet-700 dark:text-[#a78bfa]',
      activeBar:    'from-[#a78bfa] to-[#818cf8]',
      activeIcon:   'text-[#a78bfa]',
      activeBadge:  'bg-[#a78bfa]/20 text-[#a78bfa]',
    },
    EVENTS: {
      color:        'text-emerald-500 dark:text-[#10d98e]',
      dot:          'bg-emerald-500 dark:bg-[#10d98e]',
      activeBg:     'bg-emerald-50 dark:bg-[#10d98e]/10',
      activeText:   'text-emerald-700 dark:text-[#10d98e]',
      activeBar:    'from-[#10d98e] to-[#06b6d4]',
      activeIcon:   'text-[#10d98e]',
      activeBadge:  'bg-[#10d98e]/20 text-[#10d98e]',
    },
  };

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-50 w-64
        bg-white dark:bg-[#0b1020]
        border-r border-gray-200/80 dark:border-[#1d2d47]
        flex flex-col select-none
        transition-transform duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}
    >
      {/* Subtle radial glow in top-left corner (dark only) */}
      <div className="pointer-events-none absolute top-0 left-0 w-32 h-32 rounded-full bg-[#4f8ff8] opacity-[0.04] blur-2xl dark:opacity-[0.07]" />

      {/* ── Logo area ── */}
      <div className="flex items-center h-[64px] border-b border-gray-200/80 dark:border-[#1d2d47] shrink-0">
        <NavLink
          to="/scanner"
          onClick={onClose}
          className="flex-1 flex items-center h-full overflow-hidden hover:opacity-95 transition-opacity min-w-0"
        >
          <LogoBanner />
        </NavLink>

        {/* Mobile-only close button */}
        <button
          onClick={onClose}
          className="md:hidden shrink-0 p-2 mr-2 rounded-lg text-gray-400 dark:text-[#a3b6cc] hover:text-gray-700 dark:hover:text-[#d5deea] hover:bg-gray-100 dark:hover:bg-[#141e30] transition-colors"
          aria-label="Close menu"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* ── Version bar with gradient accent ── */}
      <div className="relative px-4 py-2.5 border-b border-gray-200/60 dark:border-[#1d2d47] bg-gradient-to-r from-blue-50/80 via-violet-50/40 to-white/80 dark:from-[#0e1624]/80 dark:via-[#0f1832]/50 dark:to-[#0b1020]/80 shrink-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-[#4f8ff8] via-[#a78bfa] to-[#10d98e] opacity-40" />
        <div className="flex items-center justify-between">
          <span className="text-[9.5px] font-mono tracking-[0.15em] text-gray-500 dark:text-[#a3b6cc] uppercase font-semibold">
            Momentum Scanner
          </span>
          <span className="text-[8px] font-mono font-bold bg-gradient-to-r from-[#4f8ff8] to-[#6366f1] bg-clip-text text-transparent border border-[#4f8ff8]/30 px-1.5 py-0.5 rounded">
            v0.4
          </span>
        </div>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5 scrollbar-none">
        {categories.map((cat) => {
          const cfg = categoryConfig[cat];
          const items = menuItems.filter(item => item.category === cat);

          return (
            <div key={cat}>
              {/* Category header */}
              <div className="flex items-center gap-2 px-2 mb-2">
                <span className={`w-1 h-1 rounded-full ${cfg.dot} opacity-70`} />
                <h5 className={`text-[9.5px] font-semibold tracking-[0.15em] uppercase font-mono ${cfg.color} opacity-80`}>
                  {cat}
                </h5>
              </div>

              <div className="space-y-0.5">
                {items.map((item) => {
// זיהוי דינמי של כל קישור עם סולמית (גם gainers וגם losers)
                  const isHashLink = item.path.includes('#');
                  const targetHash = isHashLink ? item.path.split('#')[1] : '';
                  
                  const isActive = isHashLink
                    ? location.pathname === '/scanner' && location.hash === `#${targetHash}`
                    : location.pathname === item.path;

                  const handleClick = (e: React.MouseEvent) => {
                    if (isHashLink) {
                      e.preventDefault();
                      onClose();
                      const scrollToHash = () => {
                        const el = document.getElementById(targetHash);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      };
                      if (location.pathname === '/scanner') {
                        navigate(`/scanner#${targetHash}`, { replace: true });
                        // Re-clicking the same hash still needs an explicit scroll
                        requestAnimationFrame(scrollToHash);
                      } else {
                        navigate(`/scanner#${targetHash}`);
                      }
                    } else if (item.path === '/scanner') {
                      onClose();
                      if (location.pathname === '/scanner') {
                        e.preventDefault();
                        if (location.hash) navigate('/scanner', { replace: true });
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    } else {
                      onClose();
                    }
                  };

                  return (
                    <NavLink
                      key={item.name}
                      to={item.path}
                      onClick={handleClick}
                      className={`
                        group relative flex items-center justify-between
                        px-3 py-2.5 rounded-lg text-[13px]
                        transition-all duration-150 cursor-pointer
                        ${isActive
                          ? `${cfg.activeBg} ${cfg.activeText} font-medium`
                          : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea] hover:bg-gray-50 dark:hover:bg-[#141e30]'
                        }
                      `}
                    >
                      {/* Active left bar — category-colored gradient */}
                      {isActive && (
                        <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-gradient-to-b ${cfg.activeBar}`} />
                      )}

                      <div className="flex items-center gap-3 min-w-0">
                        {/* Icon */}
                        <span className={`
                          shrink-0 w-[18px] h-[18px] flex items-center justify-center
                          transition-colors duration-150
                          ${isActive
                            ? cfg.activeIcon
                            : 'text-gray-400 dark:text-[#a3b6cc] group-hover:text-gray-600 dark:group-hover:text-[#c2d0e0]'
                          }
                        `}>
                          {item.icon}
                        </span>

                        {/* Name */}
                        <span className="truncate font-medium tracking-tight">{item.name}</span>
                      </div>

                      {/* Badge */}
                      {item.badge !== undefined && (
                        <span className={`
                          shrink-0 px-1.5 py-0.5 rounded-full
                          text-[9px] font-mono font-semibold tabular-nums tracking-wide
                          transition-colors duration-150
                          ${isActive
                            ? cfg.activeBadge
                            : 'bg-gray-100 dark:bg-[#1d2d47] text-gray-500 dark:text-[#a3b6cc] group-hover:bg-gray-200 dark:group-hover:bg-[#263757]'
                          }
                        `}>
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* ── Footer status bar ── */}
      <div className="shrink-0 px-4 py-3 border-t border-gray-200/60 dark:border-[#1d2d47] bg-gradient-to-r from-emerald-50/60 via-white/40 to-white/60 dark:from-[#10d98e]/5 dark:via-[#0e1624]/40 dark:to-[#0b1020]/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 dark:bg-[#10d98e]" />
            </span>
            <span className="text-[10.5px] font-semibold text-emerald-700 dark:text-[#10d98e]">
              Scanner Active
            </span>
          </div>
          <span className="text-[9px] font-mono font-bold tracking-widest text-emerald-500 dark:text-[#10d98e]/60 uppercase">
            LIVE
          </span>
        </div>
      </div>
    </aside>
  );
}